import { Extension } from '@tiptap/core';
import { firestoreCollaborationPlugin } from './firestoreCollaborationPlugin';
import {
  ListenToDocumentUpdates,
  SendDocumentUpdateSteps,
  SaveDocumentContent,
  GetInitialDocumentContentAndVersion,
} from './types';

export interface FirestoreCollaborationOptions {
  clientID: string;
  debounceTime?: number;
  listenToDocumentUpdates: ListenToDocumentUpdates | undefined;
  sendDocumentUpdateSteps: SendDocumentUpdateSteps | undefined;
  saveDocumentContent: SaveDocumentContent | undefined;
  getInitialDocumentContent: GetInitialDocumentContentAndVersion | undefined;
  versionListener?: (version: number) => void;
}

/**
 * An extension for ProseMirror that enables real-time collaboration using Firestore as the backend.
 *
 * @typeParam FirestoreCollaborationOptions - The options for the FirestoreCollaborationExtension.
 * @param name - The name of the extension.
 * @param {number} priority - The priority of the extension.
 * @param {() => FirestoreCollaborationOptions} addOptions - A function that returns the options for the extension.
 * @param {() => ProseMirrorPlugin[]} addProseMirrorPlugins - A function that returns an array of ProseMirror plugins to be added to the editor.
 * @returns {Extension<FirestoreCollaborationOptions>} - The FirestoreCollaborationExtension.
 */
export const FirestoreCollaborationExtension =
  Extension.create<FirestoreCollaborationOptions>({
    name: 'firestoreCollab',
    priority: 1000,
    addOptions() {
      return {
        clientID: Math.floor(Math.random() * 0xffffffff).toString(),
        listenToDocumentUpdates: undefined,
        sendDocumentUpdateSteps: undefined,
        saveDocumentContent: undefined,
        getInitialDocumentContent: undefined,
        versionListener: undefined,
      };
    },
    addProseMirrorPlugins() {
      const {
        clientID,
        debounceTime,
        listenToDocumentUpdates,
        sendDocumentUpdateSteps,
        saveDocumentContent,
        getInitialDocumentContent,
        versionListener,
      } = this.options;

      if (
        !clientID ||
        !listenToDocumentUpdates ||
        !sendDocumentUpdateSteps ||
        !saveDocumentContent ||
        !getInitialDocumentContent
      ) {
        throw new Error(
          'FirestoreCollaborationExtension requires all options to be set',
        );
      }

      return [
        firestoreCollaborationPlugin({
          clientID,
          debounceTime,
          listenToDocumentUpdates,
          sendDocumentUpdateSteps,
          saveDocumentContent,
          getInitialDocumentContent,
          versionListener,
        }),
      ];
    },
  });
